import { XMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A support-chat image attachment (#1808): a small rounded thumbnail that darkens
 * on hover and opens a full-screen lightbox on click. Shared between the composer
 * preview and the rendered DM thread so both sides look identical.
 *
 * `onRemove`, when given, adds a hover ✕ in the corner (composer preview only) —
 * the thread render omits it.
 */
export default function ImageAttachment({
  url,
  alt = 'Attached image',
  onRemove,
  className,
}: {
  url: string;
  alt?: string;
  onRemove?: () => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className={`group/att relative inline-block ${className ?? ''}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="block overflow-hidden rounded-lg ring-1 ring-zinc-900/10 dark:ring-white/10"
          aria-label="Open image"
        >
          <img
            src={url}
            alt={alt}
            className="h-auto max-h-40 w-auto max-w-[12rem] object-cover transition group-hover/att:brightness-75"
          />
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
            title="Remove file"
            aria-label="Remove file"
            className="absolute -right-2 -top-2 hidden rounded-full bg-zinc-900 p-1 text-white shadow-md ring-2 ring-white group-hover/att:block hover:bg-zinc-700 dark:ring-zinc-900"
          >
            <XMarkIcon className="size-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
      {open && <Lightbox url={url} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

function Lightbox({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="presentation"
      aria-label={alt}
    >
      <button
        type="button"
        onClick={onClose}
        title="Close"
        aria-label="Close"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <XMarkIcon className="size-6" aria-hidden="true" />
      </button>
      <img
        src={url}
        alt={alt}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[90dvh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
      />
    </div>,
    document.body,
  );
}
